import * as React from 'react';

import { cn } from '../../lib/utils';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from './dialog';
import { Input } from './input';
import { modalChrome, type ModalSize } from './modal-chrome';

export type { ModalSize } from './modal-chrome';

export interface ModalProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Footer content (usually action buttons). Omit for a footer-less modal. */
  footer?: React.ReactNode;
  size?: ModalSize;
  showClose?: boolean;
  /**
   * Whether Esc / backdrop click can dismiss the modal (default true). Set false
   * for a forced modal that must be resolved through an explicit action — the
   * close affordance is also hidden.
   */
  dismissable?: boolean;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Set inside a `full` cover. Base UI skips the backdrop of a nested dialog on the
 * assumption its parent already dims, but a cover draws none — without its own
 * backdrop the nested modal has nothing above the cover to receive outside
 * presses, so a text selection dragged out of the card closes it.
 */
const InsideCoverContext = React.createContext(false);

/**
 * Modal —— OverlaySurface 的 `modal` presenter 与声明档共用的标准壳（Dialog 高层
 * 封装）：统一的标题/正文/footer 结构、`size`（`sm`–`xl` 居中卡片，`full` 铺满窗口、
 * 无遮罩/关闭键）、`showClose`、以及 `dismissable` 强制语义。内容（`children`）由使用
 * 方决定；平台/模块只填内容，不重复写遮罩与定位。
 */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  footer,
  size = 'md',
  showClose = true,
  dismissable = true,
  className,
  children,
}: ModalProps): React.ReactNode {
  const { cover, contentClass } = modalChrome(size);
  const insideCover = React.useContext(InsideCoverContext);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // A forced (non-dismissable) modal ignores Esc / backdrop close; only an
        // explicit action may resolve it. Cover shells have no backdrop; Esc
        // still follows `dismissable`.
        if (!next && !dismissable) {
          return;
        }
        onOpenChange?.(next);
      }}
    >
      {insideCover && !cover ? (
        <DialogPortal>
          <DialogOverlay forceRender />
        </DialogPortal>
      ) : null}
      <DialogContent
        showCloseButton={!cover && showClose && dismissable}
        showOverlay={!cover && !insideCover}
        className={cn(contentClass, className)}
      >
        {!cover && (title || description) ? (
          <DialogHeader className="shrink-0">
            {title ? <DialogTitle>{title}</DialogTitle> : null}
            {description ? (
              <DialogDescription>{description}</DialogDescription>
            ) : null}
          </DialogHeader>
        ) : null}
        {children ? (
          <div
            className={
              cover
                ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
                : 'min-h-0 flex-1 overflow-y-auto'
            }
          >
            <InsideCoverContext.Provider value={cover}>
              {children}
            </InsideCoverContext.Provider>
          </div>
        ) : null}
        {!cover && footer ? (
          <DialogFooter className="shrink-0">{footer}</DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  title?: React.ReactNode;
  message?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** Style the confirm button as destructive (e.g. delete). */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * 声明式确认框（OverlaySurface 声明档 `role:'confirm'`）。文案由调用方传入（ui-client
 * 保持 i18n 无关），默认英文兜底。
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmText = 'OK',
  cancelText = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): React.ReactNode {
  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
      title={title}
      description={message}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onCancel}>
            {cancelText}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            autoFocus
            onClick={onConfirm}
          >
            {confirmText}
          </Button>
        </>
      }
    />
  );
}

export interface AlertDialogProps {
  open: boolean;
  title?: React.ReactNode;
  message?: React.ReactNode;
  confirmText?: string;
  onClose: () => void;
}

/** 声明式提示框（OverlaySurface 声明档 `role:'alert'`）：单个确认按钮。 */
export function AlertDialog({
  open,
  title,
  message,
  confirmText = 'OK',
  onClose,
}: AlertDialogProps): React.ReactNode {
  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
      title={title}
      description={message}
      size="sm"
      footer={
        <Button autoFocus onClick={onClose}>
          {confirmText}
        </Button>
      }
    />
  );
}

export interface PromptDialogProps {
  open: boolean;
  title?: React.ReactNode;
  message?: React.ReactNode;
  defaultValue?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}

/** 声明式输入框（OverlaySurface 声明档 `role:'prompt'`）：单行输入 + 确认/取消。 */
export function PromptDialog({
  open,
  title,
  message,
  defaultValue = '',
  placeholder,
  confirmText = 'OK',
  cancelText = 'Cancel',
  onSubmit,
  onCancel,
}: PromptDialogProps): React.ReactNode {
  const [value, setValue] = React.useState(defaultValue);

  // Reset the field whenever the dialog (re)opens so a reused instance never
  // shows the previous prompt's text.
  React.useEffect(() => {
    if (open) {
      setValue(defaultValue);
    }
  }, [open, defaultValue]);

  const submit = (): void => {
    onSubmit(value);
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
      title={title}
      description={message}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onCancel}>
            {cancelText}
          </Button>
          <Button autoFocus onClick={submit}>
            {confirmText}
          </Button>
        </>
      }
    >
      <Input
        autoFocus
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            submit();
          }
        }}
      />
    </Modal>
  );
}

export interface SelectDialogOption {
  value: string;
  label: string;
  description?: string;
}

export interface SelectDialogProps {
  open: boolean;
  title?: React.ReactNode;
  message?: React.ReactNode;
  options: readonly SelectDialogOption[];
  placeholder?: string;
  cancelText?: string;
  emptyText?: string;
  onSelect: (value: string) => void;
  onCancel: () => void;
}

/**
 * 声明式单选框（OverlaySurface 声明档 `role:'select'`）：可过滤的候选列表，选中即
 * 回传，无确认按钮。补齐 confirm/alert/prompt 之外的「从若干项里挑一个」——带参
 * 命令自己收参时最常缺的那一档。
 */
export function SelectDialog({
  open,
  title,
  message,
  options,
  placeholder,
  cancelText = 'Cancel',
  emptyText = 'No matches.',
  onSelect,
  onCancel,
}: SelectDialogProps): React.ReactNode {
  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
      title={title}
      description={message}
      size="sm"
      footer={
        <Button variant="outline" onClick={onCancel}>
          {cancelText}
        </Button>
      }
    >
      <Command>
        <CommandInput autoFocus placeholder={placeholder} />
        <CommandList>
          <CommandEmpty>{emptyText}</CommandEmpty>
          {options.map((option) => (
            <CommandItem
              key={option.value}
              // cmdk filters on this string and hands it back to `onSelect`, so
              // the raw value is invoked from the closure instead.
              value={`${option.label} ${option.value}`}
              onSelect={() => onSelect(option.value)}
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{option.label}</span>
                {option.description ? (
                  <span className="text-muted-foreground truncate text-xs">
                    {option.description}
                  </span>
                ) : null}
              </span>
            </CommandItem>
          ))}
        </CommandList>
      </Command>
    </Modal>
  );
}
